import React, { useMemo, useState } from "react";
import { Platform, View, Text, Pressable, FlatList } from "react-native";
import { Tooltip } from "react-native-paper";
import { G, Svg } from "react-native-svg";
import { VictoryPie, VictoryLabel } from "victory-native";
import { SvgLegend } from "./SvgLegend";

type InputItem = { name: string; value: number; color: string };

const truncate = (s: string, max: number) => (s.length > max ? `${s.slice(0, max - 1)}…` : s);
type LegendItem = { label: string; color: string };

export default function PieChartExportable({
    input,
    donutRadius,
    chartId,
    width,
    height,
    mode = "screen",
    layout = "sideLegend",
}: {
    input: InputItem[];
    donutRadius?: number;
    chartId?: string;
    width?: number;
    height?: number;
    mode?: "screen" | "export";
    layout?: "bottomLegend" | "sideLegend";
}) {
    const hasData = !!input?.length;

    const W = width ?? (Platform.OS === "web" ? 520 : 360);
    const baseH = height ?? 360; // card height stays 360 for screen usage

    // Sort biggest first for readability
    const items = useMemo(() => {
        if (!hasData) return [];
        return [...input].filter(i => i?.value > 0).sort((a, b) => b.value - a.value);
    }, [hasData, input]);

    const pieData = hasData ? items.map(i => ({ x: i.name, y: i.value })) : [{ x: "No Data", y: 1 }];
    const colors = hasData ? items.map(i => i.color) : ["#f0f0f0"];

    // Put these near the top (after items/colors computed)

    //const W = Math.max(600, Number(width) || 600);

    // Chart area on top, legend area at bottom
    const chartAreaH = 420; // enough for donut + padding
    const rowH = 26;
    const dot = 10;
    const gapX = 14;
    const padX = 24;

    // Approx text width for wrapping (no canvas measure in RN easily)
    const estTextW = (label: string) => Math.min(420, label.length * 7 + 8);

    // Build legend items from items if legendItems not passed
    const total = items.reduce((sum, i) => sum + (i.value ?? 0), 0);
    const pct = (v: number) => (total > 0 ? (v / total) * 100 : 0);
    const fmtPct = (p: number) => `${Math.round(p)}%`; // or p.toFixed(1)+"%"

    const legend: LegendItem[] = useMemo(() => {
        return items.map((i) => {
            const v = Number(i.value) || 0;
            const p = fmtPct(pct(v));
            return {
                color: i.color,
                label: `${i.name}: ${v} (${p})`, // ✅ count + %
            };
        });
    }, [items, total]);

    // Wrap legend into rows
    const legendRows: LegendItem[][] = useMemo(() => {
        const rows: LegendItem[][] = [];
        let cur: LegendItem[] = [];
        let x = 0;
        const maxW = W - padX * 2;

        for (const it of legend) {
            const w = dot + 8 + estTextW(it.label) + gapX;
            if (cur.length && x + w > maxW) {
                rows.push(cur);
                cur = [];
                x = 0;
            }
            cur.push(it);
            x += w;
        }
        if (cur.length) rows.push(cur);
        return rows;
    }, [legend, W]);

    const legendAreaH = legendRows.length ? legendRows.length * rowH + 18 : 0;

    // Final export SVG height
    const H = chartAreaH + legendAreaH;

    // Pie sizing (top centered)
    const cx = W / 2;
    const cy = chartAreaH / 2 + 10;
    const radius = Math.min(W, chartAreaH) / 2 - 28;
    const innerR = donutRadius ? Math.max(24, radius * 0.35) : 0;

    // Column widths
    const gap = 10;
    const legendColW = layout === "sideLegend" ? Math.max(150, Math.floor(W * 0.38)) : 0;
    const pieColW = layout === "sideLegend" ? W - legendColW - gap : W;

    // Legend paging (screen)
    const pageSize = layout === "sideLegend" ? 10 : 9;
    const [page, setPage] = useState(0);

    const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
    const pageItems = mode === "export" ? items : items.slice(page * pageSize, (page + 1) * pageSize);

    // Export height: keep same card height visually, but if you use hidden export render,
    // you can increase height to show ALL legend items without paging.
    const computedExportH =
        mode === "export" && layout === "sideLegend"
            ? Math.max(baseH, 40 + items.length * 20) // tall enough for full legend list
            : baseH;

    //const W = Math.max(600, Number(width) || 600);
    // Pie sizing inside its column
    const topPad = 10;
    const sidePad = 10;
    const pieH = H - topPad;
    //const radius = Math.min(pieColW, pieH) / 2 - sidePad;
    //const innerR = donutRadius ? Math.max(20, radius * 0.35) : 0;
    const screenH = baseH;
    const pieCenterX = pieColW / 2;
    const pieCenterY = pieH / 2 + topPad / 2;
    const [hoverTip, setHoverTip] = React.useState<null | { text: string; y: number; rowH: number; }>(null);

    const availablePieH = screenH - topPad;
    const screenRadius = Math.min(pieColW, availablePieH) / 2 - sidePad;
    const screenInnerR = donutRadius ? Math.max(20, screenRadius * 0.35) : 0;

    //const pieCenterX = pieColW / 2;
    //const pieCenterY = availablePieH / 2 + topPad / 2;

    const LegendRow = ({ item }: { item: any }) => {
        const fullText = `${item.name}: ${item.value}`;

        return (
            <Tooltip title={`${item.name}: ${item.value}`}>
                <Pressable
                    onLayout={(e) => {
                        const { y, height } = e.nativeEvent.layout;
                        // store latest layout; we only show it when hovering
                        // (no state update here to avoid extra renders)
                        (item as any).__layout = { y, height };
                    }}
                    {...(Platform.OS === "web"
                        ? {
                            onHoverIn: () => {
                                const layout = (item as any).__layout;
                                if (!layout) return;
                                setHoverTip({ text: fullText, y: layout.y, rowH: layout.height });
                            },
                            onHoverOut: () => setHoverTip(null),
                        }
                        : {})}
                    style={{ flexDirection: "row", alignItems: "center", marginBottom: 10 }}
                >
                    <View
                        style={{
                            width: 10,
                            height: 10,
                            borderRadius: 6,
                            backgroundColor: item.color,
                            marginRight: 8,
                        }}
                        {...(Platform.OS === "web"
                            ? { onMouseLeave: () => setHoverTip(null) }
                            : {})}
                    />
                    <Text numberOfLines={1} style={{ fontSize: 12, flex: 1 }}>
                        {truncate(fullText, 28)}
                    </Text>
                </Pressable>
            </Tooltip>
        );
    };

    if (mode === "export") {
        return (
            <View style={{ width: W, height: H }}>
                <Svg
                    id={`${chartId}-svg`}
                    width={W}
                    height={H}
                    viewBox={`0 0 ${W} ${H}`}
                    preserveAspectRatio="xMidYMid meet"
                    style={{ overflow: "visible" }}
                >
                    {/* PIE (top center) */}
                    <G>
                        <VictoryPie
                            standalone={false}
                            width={W}
                            height={chartAreaH}
                            data={pieData}
                            colorScale={colors}
                            style={{ labels: { display: "none" }, data: { stroke: "#fff", strokeWidth: 1 } }}
                            innerRadius={innerR}
                            radius={radius}
                            origin={{ x: cx, y: cy }}
                            animate={undefined} // ✅ don’t animate in export
                        />

                        {!hasData && (
                            <VictoryLabel
                                text="No data available"
                                x={W / 2}
                                y={chartAreaH / 2}
                                textAnchor="middle"
                                style={{ fontSize: 14, fill: "gray" }}
                            />
                        )}
                    </G>

                    {/* LEGEND (bottom, centered, wrapping) */}
                    {legendRows.length > 0 && (
                        <G transform={`translate(${padX}, ${chartAreaH + 10})`}>
                            {legendRows.map((row, rowIndex) => {
                                // compute row width to center it
                                const rowWidth = row.reduce((acc, it) => {
                                    return acc + dot + 8 + estTextW(it.label) + gapX;
                                }, 0);
                                const offsetX = Math.max(0, (W - padX * 2 - rowWidth) / 2);

                                let cursorX = offsetX;
                                const y = rowIndex * rowH;

                                return (
                                    <G key={`row-${rowIndex}`} transform={`translate(0, ${y})`}>
                                        {row.map((it, i) => {
                                            const itemW = dot + 8 + estTextW(it.label) + gapX;
                                            const x = cursorX;
                                            cursorX += itemW;

                                            return (
                                                <G key={`${it.label}-${i}`} transform={`translate(${x}, 0)`}>
                                                    {/* color dot */}
                                                    <G>
                                                        <circle cx={dot / 2} cy={dot / 2 + 6} r={dot / 2} fill={it.color} />
                                                    </G>
                                                    {/* label */}
                                                    <VictoryLabel
                                                        text={it.label}
                                                        x={dot + 10}
                                                        y={dot / 2 + 10}
                                                        textAnchor="start"
                                                        style={{ fontSize: 14, fill: "#111" }}
                                                    />
                                                </G>
                                            );
                                        })}
                                    </G>
                                );
                            })}
                        </G>
                    )}
                </Svg>
            </View>
        );
    }
    return (
        <View style={{ width: W, height: screenH }}>
            {layout === "sideLegend" ? (
                <View style={{ flexDirection: "row", height: screenH }}>
                    {/* LEFT: PIE */}
                    <View style={{ width: pieColW }}>
                        <Svg
                            id={`${chartId}-s-svg`}             // ✅ THIS is what runExport grabs
                            width={W}
                            height={screenH}
                            viewBox={`0 0 ${W} ${screenH}`}
                            preserveAspectRatio="xMidYMid meet"
                            style={{ overflow: "hidden" }}
                        >
                            <G>
                                <VictoryPie
                                    standalone={false}
                                    width={pieColW}
                                    height={screenH}
                                    data={pieData}
                                    colorScale={colors}
                                    style={{
                                        labels: { display: "none" },
                                        data: { stroke: "#fff", strokeWidth: 1 },
                                    }}
                                    innerRadius={screenInnerR}
                                    radius={screenRadius}
                                    origin={{ x: pieCenterX, y: pieCenterY }}
                                    animate={{ duration: 600 }}
                                />
                            </G>
                            {!hasData && (
                                <VictoryLabel
                                    text="No data available"
                                    x={pieColW / 2}
                                    y={screenH / 2}
                                    textAnchor="middle"
                                    style={{ fontSize: 14, fill: "gray" }}
                                />
                            )}
                        </Svg>
                    </View>

                    {/* GAP */}
                    <View style={{ width: gap }} />

                    {/* RIGHT: LEGEND */}
                    <View style={{ width: legendColW, paddingRight: 6, paddingTop: 10 }}>
                        <View style={{ position: "relative" }}>
                            <FlatList
                                data={pageItems}
                                keyExtractor={(it, idx) => `${it.name}-${idx}`}
                                renderItem={({ item }) => <LegendRow item={item} />}
                            />
                            {Platform.OS === "web" && hoverTip && (
                                <View
                                    style={{
                                        position: "absolute",
                                        left: 0,
                                        right: 0,
                                        // place tooltip ABOVE the hovered row
                                        top: Math.max(0, hoverTip.y - 34),
                                        alignSelf: "flex-start",
                                        paddingVertical: 6,
                                        paddingHorizontal: 8,
                                        borderRadius: 8,
                                        backgroundColor: "rgba(0,0,0,0.85)",
                                        zIndex: 9999,
                                        pointerEvents: "none", // ✅ critical: tooltip doesn't steal hover
                                    }}
                                >
                                    <Text style={{ color: "white", fontSize: 12 }} numberOfLines={2}>
                                        {hoverTip.text}
                                    </Text>
                                </View>
                            )}
                        </View>


                        {/* Pagination (screen only) */}
                        {mode === "screen" && pageCount > 1 && (
                            <View style={{ flexDirection: "row", justifyContent: "space-between", paddingTop: 10 }}>
                                <Pressable onPress={() => setPage(p => (p - 1 + pageCount) % pageCount)}>
                                    <Text style={{ fontSize: 12 }}>Prev</Text>
                                </Pressable>
                                <Text style={{ fontSize: 11, color: "gray" }}>{`Page ${page + 1}/${pageCount}`}</Text>
                                <Pressable onPress={() => setPage(p => (p + 1) % pageCount)}>
                                    <Text style={{ fontSize: 12 }}>Next</Text>
                                </Pressable>
                            </View>
                        )}
                    </View>
                </View>
            ) : (
                // keep your existing bottomLegend version here if you want
                <Text>bottomLegend layout not shown here</Text>
            )}
        </View>
    );
}
